import React, { useCallback, useMemo, useState } from 'react';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import MaterialSelect from '@material-ui/core/Select';
import PropTypes from 'prop-types';

import FormControl from '../form-control/form-control';
import DropdownArrow from '../icons/dropdown-arrow/dropdown-arrow';

import './select.less';

const SELECT_CLASSES = { root: 'material-select' };

// for fixing console error loop on using Select inside a Dialog,
// better fix is using disablePortal prop. but setting the disablePortal to true requires some design alignment for making the dropdown the top view level wherever used
// more info: https://stackoverflow.com/questions/54133326/material-ui-uncaught-rangeerror-maximum-call-stack-size-exceeded
const MENU_PROPS = { disableEnforceFocus: true };

const Select = props => {
	const { options, variant, label, className, fullWidth, required, error } = props;
	const [open, setOpen] = useState(false);
	const [labelWidth, setLabelWidth] = useState(0);
	const onClose = useCallback(() => setOpen(false), []);
	const onOpen = useCallback(() => setOpen(true), []);
	const DropdownArrowComp = useCallback(() => <DropdownArrow open={open} />, [open]);

	const selectInputComponent = useMemo(() => {
		const isOutlined = variant === 'outlined';
		const InputComp = variant === 'outlined' ? OutlinedInput : Input;
		const addedProps = {
			...(!isOutlined && { disableUnderline: true }),
		};
		return (
			<InputComp labelwidth={labelWidth} name="label" id="label" label={label} {...addedProps} />
		);
	}, [label, labelWidth, variant]);

	const InputComponent = (
		<MaterialSelect
			classes={SELECT_CLASSES}
			onOpen={onOpen}
			onClose={onClose}
			IconComponent={DropdownArrowComp}
			{...props}
			input={selectInputComponent}
			MenuProps={MENU_PROPS}
		>
			{options.map(option => (
				<MenuItem key={option.key} value={option.key}>
					{option.value}
				</MenuItem>
			))}
		</MaterialSelect>
	);

	return (
		<FormControl
			className={`material-select-form-control ${className}`}
			fullWidth={fullWidth}
			label={label}
			inputComponent={InputComponent}
			required={required}
			setLabelWidth={setLabelWidth}
			error={error !== undefined ? error : false}
		/>
	);
};

Select.defaultProps = {
	label: undefined,
	className: '',
	variant: undefined,
	fullWidth: false,
	error: false,
	MenuProps: {
		style: { zIndex: 1302 },
		anchorOrigin: {
			vertical: 'bottom',
			horizontal: 'center',
		},
		transformOrigin: {
			vertical: 0,
			horizontal: 'center',
		},
		getContentAnchorEl: null,
		PopoverClasses: { root: 'select-menu-popover', paper: 'menu-select-paper' },
	},
};

Select.propTypes = {
	...MaterialSelect.propTypes,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.any.isRequired,
			value: PropTypes.string.isRequired,
		}),
	).isRequired,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	variant: PropTypes.string,
	className: PropTypes.string,
	fullWidth: PropTypes.bool,
	error: PropTypes.bool,
	MenuProps: PropTypes.object,
};

export default Select;
